/**
 * Stellplätze × Tage – kein zweiter Kalender, sondern derselbe in einem anderen Modus: Kopf, Maße
 * und Handy-Dichte wie bei den Apartments (`geometryFor`). Im Spaltenkopf steht „Alle“ mit drei
 * Zuständen (keiner, einige, alle).
 */
import { Check, Minus } from 'lucide-react';
import { useLayoutEffect, useRef } from 'react';
import type { ParkingOccupancySpotRow } from '@/shared/api';
import { useElementWidth } from '@/shared/hooks/useElementWidth';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { cn } from '@/shared/lib/cn';
import { CalendarHeader } from './CalendarHeader';
import { ParkingSpotRow } from './ParkingSpotRow';
import { geometryFor } from './PropertyCalendarGrid';

interface Props {
  spots: ParkingOccupancySpotRow[];
  dates: string[];
  today: string;
  density: 3 | 7 | 14;
  selected: ReadonlySet<string>;
  onToggle: (spotId: string) => void;
  onToggleAll: () => void;
  onOverview: (spot: ParkingOccupancySpotRow) => void;
}

export function ParkingCalendarGrid({ spots, dates, today, density, selected, ...props }: Props) {
  const mobile = useMediaQuery('(max-width: 767px)');
  const scroller = useElementWidth<HTMLDivElement>();
  const scrollNode = useRef<HTMLDivElement | null>(null);
  const geometry = geometryFor(mobile, density, scroller.width);
  const todayIndex = dates.indexOf(today);
  // Wie bei den Apartments: reicht das Fenster über die Breite, beginnt das Bild bei heute.
  useLayoutEffect(() => {
    const node = scrollNode.current;
    if (!mobile || !node || todayIndex <= 0) return;
    node.scrollLeft = todayIndex * geometry.cellMin;
  }, [mobile, todayIndex, geometry.cellMin]);
  const count = spots.filter((spot) => selected.has(spot.spotId)).length;
  const state = count === 0 ? false : count === spots.length ? true : 'mixed';
  const all = (
    <button
      type="button"
      role="checkbox"
      aria-checked={state}
      // Am Desktop ist der sichtbare Text der Name; schmal steht nur das Kästchen da.
      aria-label={geometry.compact ? 'Alle Stellplätze auswählen' : undefined}
      onClick={props.onToggleAll}
      className={cn(
        'flex min-h-11 items-center gap-2 font-sans text-xs font-bold normal-case tracking-normal',
        geometry.compact ? '-mx-3 min-w-11 justify-center' : '-ml-1 px-1',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'grid size-[22px] place-items-center rounded-[7px]',
          state ? 'bg-forest text-white' : 'border-ink-faint/50 border-[1.5px] bg-white',
        )}
      >
        {state === true && <Check size={14} strokeWidth={3} />}
        {state === 'mixed' && <Minus size={14} strokeWidth={3} />}
      </span>
      {!geometry.compact && <span className="text-ink-soft">Stellplatz · Alle</span>}
    </button>
  );
  return (
    <div
      ref={(node) => {
        scroller.ref(node);
        scrollNode.current = node;
      }}
      className="w-full max-w-full overflow-x-auto overscroll-x-contain"
      tabIndex={0}
      aria-label="Stellplatz-Belegung, horizontal scrollbar"
    >
      <div className="min-w-full">
        <CalendarHeader
          dates={dates}
          sidebarWidth={geometry.sidebarWidth}
          cellMin={geometry.cellMin}
          sidebarLabel={all}
          today={today}
          compact={mobile}
        />
        {spots.map((row) => (
          <ParkingSpotRow
            key={row.spotId}
            row={row}
            dates={dates}
            today={today}
            geometry={geometry}
            selected={selected.has(row.spotId)}
            onToggle={() => props.onToggle(row.spotId)}
            onOverview={geometry.labels === 'none' ? () => props.onOverview(row) : undefined}
          />
        ))}
      </div>
    </div>
  );
}
